import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import {
  ArrowLeft,
  HelpCircle,
  Send,
  User,
  Shield,
  Calendar,
  AlertCircle,
  CheckCircle,
  Clock,
  MessageSquare,
} from 'lucide-react';
import { SearchSelect } from '../../components/shared';
import {
  getAdminSupportTicket,
  updateAdminSupportTicket,
  replyToSupportTicket,
} from '../../services/supportTicketService';
import { formatDate, formatTime } from '../../utils/dateUtils';
import { useToastContext } from '../../contexts/ToastContext';

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

const getStatusColor = (status) => {
  const config = {
    open: 'bg-blue-100 text-blue-800',
    in_progress: 'bg-yellow-100 text-yellow-800',
    resolved: 'bg-green-100 text-green-800',
    closed: 'bg-gray-100 text-gray-800',
  };
  return config[status] || 'bg-gray-100 text-gray-800';
};

const getPriorityColor = (priority) => {
  const config = {
    low: 'bg-green-100 text-green-800',
    medium: 'bg-yellow-100 text-yellow-800',
    high: 'bg-orange-100 text-orange-800',
    urgent: 'bg-red-100 text-red-800',
  };
  return config[priority] || 'bg-gray-100 text-gray-800';
};

const AdminSupportTicketDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { success, error: showError } = useToastContext();
  const [replyText, setReplyText] = useState('');
  const [isInternal, setIsInternal] = useState(false);

  const { data: ticketResponse, isLoading, error } = useQuery({
    queryKey: ['adminSupportTicket', id],
    queryFn: () => getAdminSupportTicket(id),
    enabled: !!id,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => updateAdminSupportTicket(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['adminSupportTicket', id]);
      queryClient.invalidateQueries(['adminSupportTickets']);
      success('Ticket updated successfully');
    },
    onError: (err) => showError(err?.response?.data?.message || 'Failed to update ticket'),
  });

  const replyMutation = useMutation({
    mutationFn: (data) => replyToSupportTicket(id, data),
    onSuccess: () => {
      setReplyText('');
      setIsInternal(false);
      queryClient.invalidateQueries(['adminSupportTicket', id]);
      queryClient.invalidateQueries(['adminSupportTickets']);
      success('Reply sent successfully');
    },
    onError: (err) => showError(err?.response?.data?.message || 'Failed to send reply'),
  });

  const ticket = ticketResponse?.data;

  const handleReply = (e) => {
    e.preventDefault();
    if (!replyText.trim()) return;
    replyMutation.mutate({ response: replyText.trim(), is_internal: isInternal });
  };

  const handleStatusChange = (value) => {
    updateMutation.mutate({ id, data: { status: value } });
  };

  const handlePriorityChange = (value) => {
    updateMutation.mutate({ id, data: { priority: value } });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="bg-white rounded-lg border p-6 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">Ticket Not Found</h3>
          <p className="text-gray-600 mb-4">
            {error?.response?.data?.message || 'This support ticket could not be found.'}
          </p>
          <button
            onClick={() => navigate('/admin/support-tickets')}
            className="text-primary-600 hover:text-primary-700 font-medium"
          >
            ← Back to Support Tickets
          </button>
        </div>
      </div>
    );
  }

  const replies = ticket.ticket_replies || ticket.ticketReplies || [];
  const sortedReplies = [...replies].sort(
    (a, b) => new Date(a.created_at) - new Date(b.created_at)
  );

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
      <div className="max-w-4xl mx-auto space-y-4">
        {/* Header */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/support-tickets')}
            className="p-2 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <HelpCircle className="w-6 h-6" />
              Ticket #{String(ticket.id).slice(0, 8)}
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">{ticket.subject}</p>
          </div>
        </div>

        {/* Ticket Info Card */}
        <div className="bg-white rounded-lg border p-4 sm:p-6">
          <div className="flex flex-wrap gap-4 mb-4">
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(ticket.status)}`}>
              {ticket.status?.replace('_', ' ')}
            </span>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(ticket.priority)}`}>
              {ticket.priority}
            </span>
            <span className="text-sm text-gray-500 capitalize">{ticket.category}</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase">Requester</label>
              <div className="flex items-center gap-2 mt-1">
                <User className="w-4 h-4 text-gray-400" />
                <div>
                  <p className="font-medium text-gray-900">{ticket.user?.name || 'N/A'}</p>
                  <p className="text-sm text-gray-500">{ticket.user?.email}</p>
                </div>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase">Assigned To</label>
              <div className="flex items-center gap-2 mt-1">
                <Shield className="w-4 h-4 text-gray-400" />
                <p className="text-gray-900">{ticket.assignedTo?.name || 'Unassigned'}</p>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase">Created</label>
              <div className="flex items-center gap-2 mt-1">
                <Calendar className="w-4 h-4 text-gray-400" />
                <span>{formatDate(ticket.created_at, 'medium')} {formatTime(ticket.created_at)}</span>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="flex flex-wrap gap-3 mb-6 p-3 bg-gray-50 rounded-lg">
            <div className="w-40">
              <label className="text-xs font-medium text-gray-500 block mb-1">Status</label>
              <SearchSelect
                value={ticket.status}
                onChange={handleStatusChange}
                options={STATUS_OPTIONS}
                size="sm"
              />
            </div>
            <div className="w-40">
              <label className="text-xs font-medium text-gray-500 block mb-1">Priority</label>
              <SearchSelect
                value={ticket.priority}
                onChange={handlePriorityChange}
                options={PRIORITY_OPTIONS}
                size="sm"
              />
            </div>
            {!ticket.assigned_to && user?.id && (
              <div className="flex items-end">
                <button
                  onClick={() => updateMutation.mutate({ id, data: { assigned_to: user.id } })}
                  disabled={updateMutation.isPending}
                  className="px-3 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                >
                  Assign to me
                </button>
              </div>
            )}
          </div>

          <div>
            <label className="text-xs font-medium text-gray-500 uppercase">Description</label>
            <p className="mt-2 text-gray-700 whitespace-pre-wrap">{ticket.description}</p>
          </div>

          {ticket.resolution_notes && (
            <div className="mt-4 pt-4 border-t">
              <label className="text-xs font-medium text-gray-500 uppercase">Resolution Notes</label>
              <p className="mt-2 text-gray-700">{ticket.resolution_notes}</p>
            </div>
          )}
        </div>

        {/* Replies Thread */}
        <div className="bg-white rounded-lg border p-4 sm:p-6">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            Conversation ({sortedReplies.length} {sortedReplies.length === 1 ? 'reply' : 'replies'})
          </h3>

          <div className="space-y-4 mb-6">
            {sortedReplies.map((reply) => (
              <div
                key={reply.id}
                className={`p-4 rounded-lg ${
                  reply.response_type === 'admin'
                    ? 'bg-blue-50 border-l-4 border-blue-500'
                    : 'bg-gray-50 border-l-4 border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {reply.response_type === 'admin' ? (
                      <Shield className="w-4 h-4 text-blue-600" />
                    ) : (
                      <User className="w-4 h-4 text-gray-600" />
                    )}
                    <span className="font-medium text-gray-900">
                      {reply.responder?.name || 'Support'}
                    </span>
                    {reply.is_internal && (
                      <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-800 rounded">
                        Internal
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-gray-500">
                    {formatDate(reply.created_at, 'medium')} {formatTime(reply.created_at)}
                  </span>
                </div>
                <p className="text-gray-700 whitespace-pre-wrap">
                  {reply.response_text || reply.response}
                </p>
              </div>
            ))}
          </div>

          {/* Reply Form */}
          <form onSubmit={handleReply} className="space-y-3">
            <textarea
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder="Type your reply..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              disabled={replyMutation.isPending}
            />
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={isInternal}
                  onChange={(e) => setIsInternal(e.target.checked)}
                  className="rounded border-gray-300"
                />
                Internal note (not visible to user)
              </label>
              <button
                type="submit"
                disabled={!replyText.trim() || replyMutation.isPending}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="w-4 h-4" />
                {replyMutation.isPending ? 'Sending...' : 'Send Reply'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminSupportTicketDetail;
